import { DeckAPI, FetchAPI } from '@philips-internal/oswald-ui-components';
import Controller, { OnAfterFetch, OnClick, OnMount, OnSubmit } from '@philips-internal/oswald-ui-core/lib/client';
import { ClickEvent, FetchEvent, MountEvent, SubmitEvent } from '@philips-internal/oswald-ui-core/lib/component/types';
import { HttpService } from '@philips-internal/oswald-ui-core/lib/services';
import { applicationContext } from '@philips-internal/oswald-ui-foundation';
import { MyChartPastHistoryAliases } from '../../codegen';
import { FragmentNames } from '../../codegen';
import { APPLICATION_ENDPOINTS } from '../../constants/application-endpoints';
import { APPLICATION_ROUTES } from '../../constants/application-routes';
import { ComponentTarget, FetchResponseData, MyChartPastHistoryOtherValues, MyChartPastHistoryTypes, PastHistoryFilter, PastHistorySituationData } from '../../constants/my-chart-past-history-types';
import UserContext from '../../utils/user-context';
import BaseController from '../BaseController';
import { MessageService } from '@philips-internal/oswald-ui-core/lib/services';
import moment from 'moment';
import { ISO_DATE_FORMAT } from '@philips-internal/oswald-ui-model/lib/formatters/date/date-utils';
import { Value } from '@philips-internal/oswald-ui-core';
import { AutocompleteAPI, FilterAPI, TabWithContentAPI } from '@philips-internal/oswald-ui-components/lib/components';
import { Inject } from '@odinjs/odin';
import { defaultIfUndefined } from '../../utils/object-utils';
import { cast } from '../../utils/cast-helpers';
import { MyChartControlItems } from '../../constants/my-chart-tab-control-items';
import { MyChartTabStructure } from '../../constants/my-chart-tab-structure';

const filterNoneValues: PastHistoryFilter = {
  filterFromDate: '',
  filterUntilDate: '',
  situation: {
    situationCode: '',
    situationDescription: '',
  }
}

@Controller({ fragment: FragmentNames.MY_CHART_PAST_HISTORY })
export default class MyChartPatientHistoryController extends BaseController {

  @Inject(MyChartPastHistoryAliases.FILTER_PAST_HISTORY_LIST )
    filterListApi!: FilterAPI;

  @Inject(MyChartPastHistoryAliases.FETCH_HOSPITAL)
    fetchHospitalListApi!: FetchAPI;

  @Inject(MyChartPastHistoryAliases.FETCH_PATIENT)
    fetchPatientListApi!: FetchAPI;

  @Inject(MyChartPastHistoryAliases.DECK_HOSPITAL)
    deckHospitalAPI!: DeckAPI;

  @Inject(MyChartPastHistoryAliases.TAB_HOSPITAL)
    tabHospital!: TabWithContentAPI;

  @OnAfterFetch(MyChartPastHistoryAliases.FETCH_HOSPITAL)
  handleHospitalAfterFetch(event: FetchEvent): void {
    const data = defaultIfUndefined(event.data, []) as FetchResponseData[];
    const newData = data.map<FetchResponseData>(item => { return { ...item } });

    this.deckHospitalAPI.setData(cast(defaultIfUndefined(newData, [])));
  }

  @OnMount({ target: [ MyChartPastHistoryAliases.FETCH_PATIENT, MyChartPastHistoryAliases.FETCH_HOSPITAL ] })
  handleFetchPastHistoryLoading(event: MountEvent): void {
    const pastHistoryFilter = this.getParametersFilter() as PastHistoryFilter;
    const fetchAPI = event.target as FetchAPI;
    this.setParametersFetchList(pastHistoryFilter, fetchAPI);
  }

  @OnMount({ target: [ MyChartPastHistoryAliases.DECK_PATIENT, MyChartPastHistoryAliases.DECK_HOSPITAL ] })
  handlePastHistoryDecksLoading(event: MountEvent): void {
    const deckAPI = event.target as DeckAPI;
    const type = deckAPI.getAlias() === MyChartPastHistoryAliases.DECK_PATIENT ? MyChartPastHistoryTypes.PATIENT : MyChartPastHistoryTypes.HOSPITAL;
    this.context.set({pastHistoryType: type});
  }

  @OnMount({ target: MyChartPastHistoryAliases.FILTER_PAST_HISTORY_LIST })
  handleFilterMount(): void {
    const activeValue = 'A';
    const description = 'patient.text.active';

    const record = this.generateInitialFilter(activeValue, description);

    this.filterListApi.setData(record);
    this.filterListApi.submit();
  }

  @OnMount({ target: [ MyChartPastHistoryAliases.TAB_HOSPITAL ] })
  handleHospitalTabLoading(): void {
    const tabVisibility = this.context.get('tabVisibility') as MyChartTabStructure;
    if (tabVisibility.tabName === MyChartControlItems.PAST_HISTORY_HOSPITAL_VISIBILITY) {
      this.tabHospital.setVisible(tabVisibility.visibility);
    }
  }

  @OnClick({ target: MyChartPastHistoryAliases.BUTTON_NEW_HISTORY })
  handleBtnSendNewPastHistory(): void {
    applicationContext.redirect(APPLICATION_ROUTES.MY_CHART_FORM_PAST_HISTORY,  { pastHistorySequence: 'new', typeAction: 'new_past_history' });
  }

  @OnClick({ target: [ MyChartPastHistoryAliases.HOSPITAL_CARD, MyChartPastHistoryAliases.PATIENT_CARD] })
  handleClickCard(event: ClickEvent): void {
    const item = event.value;
    const cardState = event.target as unknown as ComponentTarget;
    const cardType = this.getCardType(cardState.state.alias);

    const route = (cardType === MyChartPastHistoryTypes.HOSPITAL) ?
      APPLICATION_ROUTES.MY_CHART_PAST_HISTORY_HOSPITAL_DETAILS : APPLICATION_ROUTES.MY_CHART_PAST_HISTORY_PATIENT_DETAILS;

    if (cardType === MyChartPastHistoryTypes.HOSPITAL) {
      HttpService.post(APPLICATION_ENDPOINTS.POST_MC_PAST_HISTORY_READ_DOCUMENT, { pastHistorySequence: item.sequence }, {});
    }

    applicationContext.redirect(route, { pastHistorySequence: item.sequence });'L'
  }

  @OnClick({ target: [ MyChartPastHistoryAliases.TAB_PATIENT ] })
  handleInitialFilterByTabPatient(): void {
    const activeValue = 'A';
    const description = 'patient.text.active';

    const record = this.generateInitialFilter(activeValue, description);

    this.filterListApi.setData(record);
    this.context.set({pastHistoryType: MyChartPastHistoryTypes.PATIENT});
  }

  @OnClick({ target: [ MyChartPastHistoryAliases.TAB_HOSPITAL ] })
  handleInitialFilterByTabHospital(): void {
    const releaseValue = 'L';
    const description = 'patient.text.released';

    const record = this.generateInitialFilter(releaseValue, description);

    this.filterListApi.setData(record);
    this.context.set({pastHistoryType: MyChartPastHistoryTypes.HOSPITAL});
  }

  @OnMount({target: [ MyChartPastHistoryAliases.AUTOCOMPLETE_SITUATION ]})
  handleLoadAutocomplete(event: MountEvent): void {
    const type = this.context.get(MyChartPastHistoryOtherValues.PAST_HISTORY_TYPE);
    const autocomplete = event.target as AutocompleteAPI;

    autocomplete.setClearable(type === MyChartPastHistoryTypes.PATIENT);
    autocomplete.setEnabled(type === MyChartPastHistoryTypes.PATIENT);
  }

  @OnSubmit({ target: MyChartPastHistoryAliases.FILTER_PAST_HISTORY_LIST })
  onSubmitFilter(response: SubmitEvent): void {
    const pastHistoryFilter = response.record as PastHistoryFilter;
    const type = this.context.get(MyChartPastHistoryOtherValues.PAST_HISTORY_TYPE);
    const fetchApi = type === MyChartPastHistoryTypes.PATIENT ? this.fetchPatientListApi : this.fetchHospitalListApi;
    this.setParametersFetchList(pastHistoryFilter, fetchApi);
  }

  private generateInitialFilter = (code: string, description: string): Record<string, Value> => {
    const situation = {
      situationCode: code,
      situationDescription: MessageService.translate(description)
    } as PastHistorySituationData;

    const filterFromDate = moment().subtract(60, 'd').format(ISO_DATE_FORMAT);
    const filterUntilDate = moment().format(ISO_DATE_FORMAT);

    this.context.set({
      params: {
        filterFromDate,
        filterUntilDate,
        situation,
      }
    });

    const record = {
      filterFromDate: filterFromDate,
      filterUntilDate: filterUntilDate,
      situation: situation
    } as Record<string, Value>;

    return record;
  }

  private getParametersFilterForFetch = (filter: PastHistoryFilter, type: string): Record<string, Value> => {
    const { patientId } = UserContext;

    const parameters = {
      endDateFormat: filter?.filterUntilDate || filterNoneValues.filterUntilDate,
      personCode: patientId.toString(),
      startDateFormat: filter?.filterFromDate || filterNoneValues.filterFromDate,
    } as Record<string, Value>;

    if(type === MyChartPastHistoryTypes.PATIENT){
      parameters[MyChartPastHistoryOtherValues.SITUATION] = filter?.situation?.situationCode || filterNoneValues.situation?.situationCode;
    }

    return parameters;
  }

  private setParametersFetchList(pastHistoryFilter: PastHistoryFilter, fetchAPI: FetchAPI): void {
    const type = this.context.get(MyChartPastHistoryOtherValues.PAST_HISTORY_TYPE) as string;
    const parameters = this.getParametersFilterForFetch(pastHistoryFilter, type);

    this.setParametersFilterContext(pastHistoryFilter);

    fetchAPI.setParameters(parameters);
    fetchAPI.forceFetch();
  }

  private setParametersFilterContext = (pastHistoryFilter: PastHistoryFilter): void => {
    this.context.set({
      params: {
        ...pastHistoryFilter
      }
    });
  }

  private getParametersFilter = (): PastHistoryFilter => {
    const params = this.context.get('params') as PastHistoryFilter;

    if(params == null){
      return {};
    }

    const pastHistoryFilter = {
      filterFromDate: params.filterFromDate,
      filterUntilDate: params.filterUntilDate,
      situation: params.situation,
    } as PastHistoryFilter;

    return pastHistoryFilter;
  }

  private getCardType(alias: string): MyChartPastHistoryTypes | undefined {
    switch(alias) {
      case MyChartPastHistoryAliases.HOSPITAL_CARD:
        return MyChartPastHistoryTypes.HOSPITAL;
      case MyChartPastHistoryAliases.PATIENT_CARD:
        return MyChartPastHistoryTypes.PATIENT;
      default:
        throw Error(`The alias ${alias} not found`);
        return undefined;
    }
  }
}
